import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getSharedStory, illustrateStory, BACKEND } from "../lib/api";
import { Loader } from "../components/Shared";
import { ArrowRight, Image as ImageIcon, MessageCircle } from "lucide-react";

export default function ShareStory() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loadingImg, setLoadingImg] = useState(false);

  useEffect(() => {
    getSharedStory(id).then(setData).catch(() => setErr("Story not found."));
  }, [id]);

  const illustrate = async () => {
    setLoadingImg(true);
    try {
      const res = await illustrateStory(id);
      setData((d) => ({ ...d, illustration_url: res.illustration_url }));
    } catch (e) {} finally { setLoadingImg(false); }
  };

  const imgSrc = data?.illustration_url ? `${BACKEND}${data.illustration_url}` : null;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="share-story-page">
      {err && <div className="text-[#D9534F]" data-testid="share-story-error">{err}</div>}
      {!data && !err && <Loader label="Loading story…" />}
      {data && (
        <article className="card p-7 md:p-10 fadeIn" data-testid="share-story-content">
          {imgSrc && (
            <div className="mb-6 rounded-2xl overflow-hidden border border-[#DCD7CE]">
              <img src={imgSrc} alt={data.title} className="w-full h-auto block" />
            </div>
          )}
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-3">Shared from EduSphere</div>
          <h1 className="font-story text-3xl md:text-5xl font-semibold text-[#1A1A1A] leading-tight">{data.title}</h1>
          <p className="font-story text-[19px] md:text-[21px] leading-[1.7] text-[#1A1A1A] mt-6 whitespace-pre-wrap">{data.story}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {!imgSrc && (
              <button onClick={illustrate} disabled={loadingImg} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60" data-testid="share-story-illustrate">
                <ImageIcon size={16} /> {loadingImg ? "Illustrating…" : "Generate illustration"}
              </button>
            )}
            <button
              onClick={() => {
                const url = `${window.location.origin.replace(/\/$/, "")}/api/og/story/${id}`.replace(window.location.origin, BACKEND);
                const text = `${data.title} — a story from EduSphere ✨\n${BACKEND}/api/og/story/${id}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
              }}
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full px-5 py-2.5 font-semibold transition"
              data-testid="share-story-whatsapp"
            >
              <MessageCircle size={16} /> WhatsApp
            </button>
            <Link to="/" className="btn-outline inline-flex items-center gap-2" data-testid="share-story-cta">
              Explore EduSphere <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="bg-[#FBE7DF] rounded-2xl p-5 border border-[#DCD7CE]">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#E07A5F] font-semibold mb-2">Moral</div>
              <div className="text-[#1A1A1A]">{data.moral}</div>
            </div>
            <div className="bg-[#EAECE6] rounded-2xl p-5 border border-[#DCD7CE]">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-2">Think with me</div>
              <div className="text-[#1A1A1A]">{data.reflection_question}</div>
            </div>
          </div>
        </article>
      )}
    </div>
  );
}
